import { FC, useEffect, useRef, useState } from "react";
import "./Sidebar.scss";
import { useStyle } from "../../providers/StyleProvider";
import { Link, useLocation } from "react-router-dom";
import { MenuModel } from "../../models/MenuModel";
import { Collapse } from "react-bootstrap";
import { useAuth } from "../../providers/AuthProvider";
import { BvIcon } from "bevi-icon";
import { canAccessDataprevFeatures } from "../../utils/dataprevAccess";

// Remove qualquer entrada de "Gerenciar Financeiras" vinda do backend/perfil — o acesso é controlado à parte por canAccessDataprevFeatures.
const DATAPREV_GATED_PATH_FRAGMENT = "simulation-financials";

const isDataprevGatedItem = (item: MenuModel): boolean =>
  !!item.path_name?.includes(DATAPREV_GATED_PATH_FRAGMENT) ||
  !!item.layout?.includes(DATAPREV_GATED_PATH_FRAGMENT);

const stripDataprevGatedMenu = (menu: MenuModel[]): MenuModel[] =>
  menu
    .filter((item) => !isDataprevGatedItem(item))
    .map((item) => (item.children ? { ...item, children: stripDataprevGatedMenu(item.children) } : item));

interface SidebarProps {}

const Sidebar: FC<SidebarProps> = () => {
  const [open, setOpen] = useState<string>("");
  const [openSubview, setOpenSubview] = useState<string>("");
  const wrapperRef = useRef<HTMLElement>(null);

  const { style } = useStyle();
  const location = useLocation();
  const { userLogged } = useAuth();

  const menu = stripDataprevGatedMenu(userLogged?.menu ?? []);
  const canManageFinancials = canAccessDataprevFeatures(userLogged?.partner_code);

  const closeSidenav = () => {
    const sidenavShow = document.getElementsByClassName("g-sidenav-pinned")[0];
    if (sidenavShow && !sidenavShow.classList.contains("g-sidenav-hidden")) {
      sidenavShow.classList.remove("g-sidenav-show");
      sidenavShow.classList.add("g-sidenav-hidden");
    }
  };

  const handleClickOutside = (event: any) => {
    if (wrapperRef.current && !wrapperRef.current?.contains(event.target)) {
      if (
        !event.target.classList.contains("sidenav-toggler-inner") &&
        !event.target.classList.contains("sidenav-toggler-line") &&
        !event.target.classList.contains("sidenav") &&
        !event.target.classList.contains("nav")
      ) {
        closeSidenav();
      }
    }
  };

  useEffect(() => {
    let parentState = "";
    let subviewState = "";

    const paths = location.pathname.split("/");

    for (const item of menu) {
      if (item.layout && `/${paths[1]}` === item.layout) {
        parentState = item.state ?? "";
      }

      if (item.children) {
        for (const child of item.children) {
          if (child.layout && `/${paths[2]}` === child.layout) {
            subviewState = child.state ?? "";
          }
        }
      }
    }

    if (parentState) {
      setOpen(parentState);
    }
    if (subviewState) {
      setOpenSubview(subviewState);
    }
  }, [location.pathname]);

  useEffect(() => {
    closeSidenav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [wrapperRef]);

  const activeRoute = (routeName: string) => {
    return location.pathname === routeName ? "active" : "";
  };

  const financialsMenuItem = (
    <li className="nav-item" key="gerenciar-financeiras">
      <Link
        to="/simulation-financials/list"
        className={`nav-link text-wrap ${activeRoute("/simulation-financials/list")}`}
      >
        <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
          <BvIcon
            name="bank"
            variant="solid"
            weight={600}
            width={20}
            height={20}
            className="text-primary text-sm opacity-10 top-0"
          />
        </div>
        <span className="nav-link-text ms-1">Gerenciar Financeiras</span>
      </Link>
    </li>
  );

  const renderList = () => {
    return menu.map((prop: MenuModel) => {
      const renderedItem = (
        <li className="nav-item" key={prop?.id}>
          {!prop.collapse ? (
            <Link
              to={prop?.path_name ?? ""}
              className={`nav-link text-wrap ${activeRoute(
                prop?.path_name ?? ""
              )}`}
            >
              {!!prop?.icon && (
                <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                  <BvIcon
                    name={prop?.icon}
                    variant="solid"
                    weight={600}
                    width={20}
                    height={20}
                    className="text-primary text-sm opacity-10 top-0"
                  />
                </div>
              )}
              <span className="nav-link-text ms-1">{prop?.name}</span>
            </Link>
          ) : (
            <>
              <a
                data-bs-toggle="collapse"
                onClick={() => {
                  if (open !== prop?.state) {
                    setOpen(prop?.state ?? "");
                    setOpenSubview("");
                  } else {
                    setOpen("");
                  }
                }}
                className={`nav-link text-wrap ${activeRoute(
                  (prop.path_name ?? "") + (prop.layout ?? "")
                )}`}
                aria-controls={prop?.state}
                role="button"
                aria-expanded="false"
              >
                {!!prop?.icon && (
                  <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                    <BvIcon
                      name={prop?.icon}
                      variant="solid"
                      weight={600}
                      width={20}
                      height={20}
                      className="text-primary text-sm opacity-10 top-0"
                    />
                  </div>
                )}
                <span className="nav-link-text ms-1">{prop?.name}</span>
              </a>

              <Collapse in={prop?.state === open}>
                <ul className="nav ms-4">
                  {prop?.children?.map((view: MenuModel) => {
                    return (
                      <li className={`nav-item`} key={view?.id}>
                        {!view?.collapse ? (
                          <Link
                            className={`nav-link text-wrap ${activeRoute(
                              (prop?.path_name ?? "") + (view?.layout ?? "")
                            )}`}
                            to={(prop?.path_name ?? "") + (view?.layout ?? "")}
                          >
                            {!!view?.icon && (
                              <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                                <BvIcon
                                  name={view?.icon}
                                  variant="solid"
                                  weight={600}
                                  width={20}
                                  height={20}
                                  className="text-primary text-sm opacity-10 top-0"
                                />
                              </div>
                            )}
                            <span className="sidenav-mini-icon">
                              {view?.name.charAt(0)}
                            </span>
                            <span className="sidenav-normal">{view?.name}</span>
                          </Link>
                        ) : (
                          <>
                            <a
                              data-bs-toggle="collapse"
                              onClick={() =>
                                setOpenSubview(
                                  openSubview !== (view?.state ?? "")
                                    ? view?.state ?? ""
                                    : ""
                                )
                              }
                              className={`nav-link text-wrap ${activeRoute(
                                (prop.path_name ?? "") + (view.layout ?? "")
                              )}`}
                              aria-controls={view?.state}
                              role="button"
                              aria-expanded="false"
                            >
                              {!!view?.icon && (
                                <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                                  <BvIcon
                                    name={view?.icon}
                                    variant="solid"
                                    weight={600}
                                    width={20}
                                    height={20}
                                    className="text-primary text-sm opacity-10 top-0"
                                  />
                                </div>
                              )}
                              <span className="nav-link-text ms-0">
                                {view?.name}
                              </span>
                            </a>

                            <Collapse in={view?.state === openSubview}>
                              <ul className="nav ms-4">
                                {view?.children?.map((item: MenuModel) => {
                                  return (
                                    <li className={`nav-item`} key={item?.id}>
                                      <Link
                                        className={`nav-link text-wrap ${activeRoute(
                                          (prop?.path_name ?? "") +
                                            (view?.path_name ?? "") +
                                            (item?.layout ?? "")
                                        )}`}
                                        to={
                                          (prop?.path_name ?? "") +
                                          (view?.path_name ?? "") +
                                          (item?.layout ?? "")
                                        }
                                      >
                                        {!!item?.icon && (
                                          <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                                            <BvIcon
                                              name={item?.icon}
                                              variant="solid"
                                              weight={600}
                                              width={20}
                                              height={20}
                                              className="text-primary text-sm opacity-10 top-0"
                                            />
                                          </div>
                                        )}
                                        <span className="sidenav-mini-icon">
                                          {item?.name.charAt(0)}
                                        </span>
                                        <span className="sidenav-normal">
                                          {item?.name}
                                        </span>
                                      </Link>
                                    </li>
                                  );
                                })}
                              </ul>
                            </Collapse>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </Collapse>
            </>
          )}
        </li>
      );

      return renderedItem;
    });
  };

  return (
    <aside
      ref={wrapperRef}
      className="sidenav bg-white navbar navbar-vertical navbar-expand-xs border-0 border-radius-xl my-3 fixed-start Sidebar"
      id="sidenav-main"
      data-testid="Sidebar"
    >
      <div className="sidenav-header d-flex align-items-center justify-content-start gap-2">
        <div className="sidenav-toggler sidenav-toggler-inner p-2">
          <button
            type="button"
            className="nav-link p-0"
            onClick={() => {
              const sidenavShow = document.getElementsByClassName("g-sidenav-pinned")[0];
              if (sidenavShow) {
                if (!sidenavShow.classList.contains("g-sidenav-hidden")) {
                  sidenavShow.classList.remove("g-sidenav-show");
                  sidenavShow.classList.add("g-sidenav-hidden");
                } else {
                  sidenavShow.classList.remove("g-sidenav-hidden");
                  sidenavShow.classList.add("g-sidenav-show");
                }
              }
            }}
          >
            <div className="sidenav-toggler-inner">
              <i className="sidenav-toggler-line bg-dark"></i>
              <i className="sidenav-toggler-line bg-dark"></i>
              <i className="sidenav-toggler-line bg-dark"></i>
            </div>
          </button>
        </div>
        <Link className="navbar-brand m-0" to="/">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <img
              alt={style?.name}
              src={`${process.env.PUBLIC_URL}/assets/img/bevi-ajuda-blue.svg`}
              className="navbar-brand-img h-100"
            />
          </div>
        </Link>
      </div>
      <div
        className="collapse navbar-collapse  w-auto h-auto"
        id="sidenav-collapse-main"
      >
        <ul className="navbar-nav">
          <li className="nav-item">
            <Link
              to={"/home"}
              className={`nav-link text-wrap ${activeRoute("/home")}`}
            >
              <div className="p-1 icon-shape icon-sm text-center d-flex align-items-center justify-content-center">
                <BvIcon
                  name="home"
                  variant="solid"
                  weight={600}
                  width={20}
                  height={20}
                  className="text-primary text-sm opacity-10 top-0"
                />
              </div>
              <span className="nav-link-text ms-1">Home</span>
            </Link>
          </li>
          {renderList()}
          {canManageFinancials && financialsMenuItem}
        </ul>
      </div>
      <div className="sidebar-back-home">
        <Link
          to="/"
          className="btn btn-primary w-100"
          onClick={closeSidenav}
        >
          Voltar para página inicial
        </Link>
      </div>
      {/* <div className="sidenav-footer mx-3 my-3">
        <div className="card card-plain shadow-none" id="sidenavCard">
          <IconDocumentation className="w-60 mx-auto" />
          <div className="card-body text-center p-3 w-100 pt-0">
            <div className="docs-info">
              <h6 className="mb-0">Precisa de Ajuda?</h6>
              <p className="text-xs font-weight-bold mb-0">Verifique a documentação</p>
            </div>
          </div>
        </div>
        <a href="https://www.creative-tim.com/learning-lab/bootstrap/overview/argon-dashboard" target="_blank" className="btn btn-dark btn-sm w-100 mb-3">Documentação</a>
      </div> */}
    </aside>
  );
};

export default Sidebar;
